import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import React, { FC, useRef } from 'react';
import styled from 'styled-components';

import { textColor, THEME_MUTED, THEME_RED, THEME_SILVER, themeColor } from '../../styles/basics/colors';

import MaterialIcon from './MaterialIcon';

export const MATERIAL_ICON_SEARCH = 'search';
export const MATERIAL_ICON_CHECK = 'check';

const MATERIAL_ICONS = [MATERIAL_ICON_CHECK, MATERIAL_ICON_SEARCH];

export const INPUT_PREFIX_ICONS = [...MATERIAL_ICONS];
export const INPUT_SUFFIX_ICONS = [...MATERIAL_ICONS];

/* TODO: discuss how to avoid width unset (collision with _004_form.scss:10) */
const StInput = styled.input<InputProps>`
    border: 0;
    outline: 0;
    border-bottom: 1px solid ${props => (props.error ? themeColor(THEME_RED) : themeColor(THEME_MUTED))};
    box-shadow: none !important;
    padding: 2px 3px !important;
    padding-left: ${props => (props.hasPrefixIcon ? '20px' : '3px')} !important;
    padding-right: ${props => (props.hasSuffixIcon ? '20px' : '3px')} !important;

    line-height: inherit;
    min-width: unset !important;
    width: ${props =>
        props.width
            ? `calc(100% - ${props.hasPrefixIcon ? '20px' : '3px'} - ${props.hasSuffixIcon ? '20px' : '3px'})`
            : 'unset'} !important;

    ::placeholder {
        ${textColor(THEME_SILVER)};
    }

    :focus::placeholder {
        color: transparent;
    }
`;

const StPrefixIcon = styled(MaterialIcon)<InputProps>`
    position: absolute;
    margin-top: 2px;
    margin-left: 2px;
    color: ${props => (props.iconColor ? props.iconColor : 'initial')};
`;

const StSuffixIcon = styled(MaterialIcon)<InputProps>`
    position: absolute;
    margin-left: calc(-1em - 2px);
    margin-top: 2px;
    color: ${props => (props.iconColor ? props.iconColor : 'initial')};
`;

const StInputContainer = styled.div<InputProps>`
    width: ${props => props.width};
    line-height: 1em;
`;

interface InputProps {
    className?: string | undefined;
    placeholder?: string;
    fontSize?: string;
    value?: any;
    type?: string;
    onEnterKey?: (e: string) => void;
    onEscapeKey?: (e: string) => void;
    onSuffixIconClick?: (value: string) => void;
    error?: boolean;
    suffixIcon?: string;
    prefixIcon?: string;
    iconColor?: string;
    width?: string;
    hasPrefixIcon?: boolean;
    hasSuffixIcon?: boolean;
    focusOnCreate?: boolean;
    disabled?: boolean;
    onChange?: (value: string) => void;
}

export const Input: FC<InputProps> = ({
    className = '',
    placeholder = '',
    type = 'text',
    suffixIcon = '',
    prefixIcon = '',
    iconColor = '',
    onSuffixIconClick,
    onChange,
    value,
    error,
    width,
    onEnterKey = () => {},
    onEscapeKey = () => {},
    focusOnCreate = false,
    disabled = false
}) => {
    let inputRef = useRef<HTMLInputElement>(null);

    const handleClick = () => {
        if (isNil(onSuffixIconClick) && typeof onSuffixIconClick === 'undefined') {
            return null;
        }
        onSuffixIconClick && onSuffixIconClick(inputRef.current?.value ?? '');
    };
    const hasPrefixIcon = !isEmpty(prefixIcon);
    const hasSuffixIcon = !isEmpty(suffixIcon);
    const prefixIconComponent = hasPrefixIcon ? <StPrefixIcon iconColor={iconColor} icon={prefixIcon} /> : null;
    const suffixIconComponent = hasSuffixIcon ? (
        <StSuffixIcon iconColor={iconColor} icon={suffixIcon} onClick={handleClick} />
    ) : null;

    const input = (
        <StInput
            ref={inputRef}
            value={value}
            data-testid="styled-input"
            type={type}
            placeholder={placeholder}
            className={hasPrefixIcon || hasSuffixIcon ? '' : className}
            width={width}
            error={error}
            hasPrefixIcon={hasPrefixIcon}
            hasSuffixIcon={hasSuffixIcon}
            onChange={event => (onChange ? onChange(event.target.value) : undefined)}
            onKeyUp={e => {
                if (e.key === 'Enter') {
                    const target = e.target as HTMLInputElement;
                    onEnterKey(target.value);
                    target.value = '';
                } else if (e.key === 'Escape') {
                    const target = e.target as HTMLInputElement;
                    onEscapeKey(target.value);
                }
            }}
            autoFocus={focusOnCreate}
            disabled={disabled}
        />
    );

    if (isNil(prefixIcon) && isNil(suffixIcon)) {
        return input;
    }

    return (
        <StInputContainer width={width} className={className}>
            {prefixIconComponent}
            {input}
            {suffixIconComponent}
        </StInputContainer>
    );
};
